/**
 * API Response Utilities
 * NestJS TransformInterceptor のエンベロープレスポンスを安全にアンラップするユーティリティ
 *
 * @description Issue #2041 - callV2API レスポンスアンラップ統合修正
 */

import logger from './logger';

/** TransformInterceptorのエンベロープ型 */
export interface TransformEnvelope<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
}

/** エンベロープ判定 type guard（statusCode + message + data の3フィールドチェック） */
export function isWrappedResponse<T>(value: unknown): value is TransformEnvelope<T> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.statusCode === 'number' &&
    obj.statusCode >= 100 &&
    obj.statusCode <= 599 &&
    typeof obj.message === 'string' &&
    'data' in obj &&
    typeof obj.timestamp === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(obj.timestamp)
  );
}

/** アンラップ（ラップ済みならdata抽出、そうでなければパススルー） */
export function unwrapResponse<T>(value: unknown, context?: string): T {
  if (isWrappedResponse<T>(value)) {
    return value.data;
  }
  // パススルー時の警告ログ（配列は正当な直接レスポンスなので警告なし）
  if (value === null || value === undefined) {
    logger.warn(`[unwrapResponse]${context ? ` ${context}:` : ''} パススルー: null/undefined を受信`, { value });
  } else if (typeof value === 'object' && !Array.isArray(value)) {
    logger.warn(`[unwrapResponse]${context ? ` ${context}:` : ''} パススルー: 非エンベロープオブジェクトを受信`, {
      keys: Object.keys(value as Record<string, unknown>),
    });
  }
  return value as T;
}

/** 配列レスポンス専用アンラップ（null/undefined/非配列はthrow） */
export function unwrapArrayResponse<T>(value: unknown, context?: string): T[] {
  const result = unwrapResponse<T[]>(value, context);
  if (result === null || result === undefined) {
    throw new Error(`[unwrapArrayResponse]${context ? ` ${context}:` : ''} null/undefined を受信`);
  }
  if (!Array.isArray(result)) {
    throw new Error(`[unwrapArrayResponse]${context ? ` ${context}:` : ''} 非配列レスポンスを受信 (type: ${typeof result})`);
  }
  return result;
}
